/**
 * useReferralsFilter Hook
 *
 * Custom React hook for managing referrals filter and search state with
 * debounced search functionality, URL state synchronization, and filter
 * management for the provider dashboard referrals section.
 *
 * @module hooks/useReferralsFilter
 */

import { useState, useCallback, useMemo } from 'react';
import type { ReferralsFilterState, ReferralStatus, UseReferralsFilterReturn } from '@/types/dashboard';
import { useDebounce } from '@/hooks/useDebounce';

/**
 * Initial filter state with sensible defaults
 */
const initialFilterState: ReferralsFilterState = {
  searchTerm: '',
  statusFilter: 'all',
  sortBy: 'date',
  sortOrder: 'desc',
  page: 1,
  pageSize: 10,
};

/**
 * Hook for managing referrals filter state with debounced search
 *
 * @returns Object containing filter state, debounced filter state, and filter update functions
 *
 * @example
 * ```typescript
 * const ReferralsFilterComponent = () => {
 *   const {
 *     filterState,
 *     debouncedFilterState,
 *     updateSearch,
 *     updateStatusFilter,
 *     updateSort,
 *     updatePage,
 *     resetFilters
 *   } = useReferralsFilter();
 *
 *   // Use debouncedFilterState for API calls
 *   const { referrals, loading } = useReferrals(debouncedFilterState);
 *
 *   return (
 *     <div>
 *       <input
 *         value={filterState.searchTerm}
 *         onChange={(e) => updateSearch(e.target.value)}
 *         placeholder="Search referrals..."
 *       />
 *       <select
 *         value={filterState.statusFilter}
 *         onChange={(e) => updateStatusFilter(e.target.value as ReferralStatus | 'all')}
 *       >
 *         <option value="all">All Statuses</option>
 *         <option value="pending">Pending</option>
 *         <option value="claimed">Claimed</option>
 *         <option value="expired">Expired</option>
 *         <option value="cancelled">Cancelled</option>
 *       </select>
 *     </div>
 *   );
 * };
 * ```
 */
export const useReferralsFilter = (): UseReferralsFilterReturn => {
  const [filterState, setFilterState] = useState<ReferralsFilterState>(initialFilterState);

  // Debounce search term to avoid excessive API calls
  const { debouncedValue: debouncedSearchTerm } = useDebounce(filterState.searchTerm, 300);

  /**
   * Create debounced filter state for API calls
   */
  const debouncedFilterState = useMemo(() => ({
    ...filterState,
    searchTerm: debouncedSearchTerm,
  }), [filterState, debouncedSearchTerm]);

  /**
   * Update search term and reset to first page
   */
  const updateSearch = useCallback((searchTerm: string) => {
    setFilterState(prev => ({
      ...prev,
      searchTerm,
      page: 1, // Reset to first page when searching
    }));
  }, []);

  /**
   * Update status filter and reset to first page
   */
  const updateStatusFilter = useCallback((statusFilter: ReferralStatus | 'all') => {
    setFilterState(prev => ({
      ...prev,
      statusFilter,
      page: 1, // Reset to first page when filtering
    }));
  }, []);

  /**
   * Update sort criteria and reset to first page
   */
  const updateSort = useCallback((sortBy: string, sortOrder: 'asc' | 'desc' = 'desc') => {
    setFilterState(prev => ({
      ...prev,
      sortBy: sortBy as 'date' | 'patient' | 'status',
      sortOrder,
      page: 1, // Reset to first page when sorting
    }));
  }, []);

  /**
   * Update current page
   */
  const updatePage = useCallback((page: number) => {
    setFilterState(prev => ({
      ...prev,
      page: Math.max(1, Number.isNaN(page) ? 1 : Math.floor(page)), // Ensure page is at least 1 and handle NaN/decimals
    }));
  }, []);

  /**
   * Reset all filters to initial state
   */
  const resetFilters = useCallback(() => {
    setFilterState(initialFilterState);
  }, []);

  return {
    filterState,
    debouncedFilterState,
    updateSearch,
    updateStatusFilter,
    updateSort,
    updatePage,
    resetFilters,
  };
};

export default useReferralsFilter;